import { deserializeCondition } from 'models/workflows/ConditionRule';
import type { ConditionRule } from 'models/workflows/ConditionRule';

const OP_LABELS: Record<string, string> = {
  eq: '=',
  neq: '≠',
  gt: '>',
  gte: '≥',
  lt: '<',
  lte: '≤',
};

function summarizeRule(rule: ConditionRule): string {
  try {
    if (rule.field !== undefined) {
      const opLabel = OP_LABELS[rule.operator ?? ''] ?? rule.operator ?? '?';
      return `${rule.field} ${opLabel} ${rule.value ?? ''}`;
    }
    if (rule.and && rule.and.length > 0) {
      return `(${rule.and.map(summarizeRule).join(' AND ')})`;
    }
    if (rule.or && rule.or.length > 0) {
      return `(${rule.or.map(summarizeRule).join(' OR ')})`;
    }
    return '?';
  } catch {
    return '?';
  }
}

/**
 * Converts a raw `conditionJson` string into a human-readable summary.
 *
 * - `undefined` / empty string  → `'Unconditional'`
 * - Unparseable JSON             → `'Invalid condition'`
 * - Leaf rule                    → `'Field = Value'`
 * - AND group                    → `'(A AND B AND …)'`
 * - OR group                     → `'(A OR B OR …)'`
 * - Unknown node shape           → `'?'`
 *
 * Never throws — all error paths return a safe string.
 */
export function summarizeCondition(conditionJson: string | undefined): string {
  if (!conditionJson || conditionJson.trim() === '') return 'Unconditional';
  const rule = deserializeCondition(conditionJson);
  if (!rule) return 'Invalid condition';
  return summarizeRule(rule);
}
